import { OnboardProperty, PropertyUnit } from "@/types/store";
import {
  PropertyFromBE,
  BathroomFromBE,
  BedroomFromBE,
  PropertyFormValues,
} from "@/types/properties";
import { USStates, USStatesByName } from "@/utils/countryStates";
import { PropertyType, SmartLockType } from "@/types/enums";
import { Amenity } from "@/api/properties/types";
import {
  BED_TYPES,
  FULL_BATHROOM_TYPES,
  HALF_BATHROOM_TYPES,
} from "@/utils/constants";

// Normalize bed type from backend to match frontend expected values
const normalizeBedType = (backendType: string | null | undefined): string => {
  if (!backendType) return "";

  // Check for exact match first
  const exactMatch = BED_TYPES.find((bed) => bed.value === backendType);
  if (exactMatch) return exactMatch.value;

  // Check for case-insensitive match
  const caseInsensitiveMatch = BED_TYPES.find(
    (bed) => bed.value.toLowerCase() === backendType.toLowerCase()
  );
  if (caseInsensitiveMatch) return caseInsensitiveMatch.value;

  // Return original value as fallback (will show as empty in select)
  return backendType;
};

// Normalize bathroom amenities from backend to match frontend expected values
const normalizeBathroomAmenities = (
  backendAmenities: string[] | null | undefined,
  isHalfBathroom: boolean
): string[] => {
  if (!backendAmenities || !Array.isArray(backendAmenities)) return [];

  const validTypes = isHalfBathroom ? HALF_BATHROOM_TYPES : FULL_BATHROOM_TYPES;

  return backendAmenities.map((amenity) => {
    // Check for exact match first
    const exactMatch = validTypes.find((type) => type.value === amenity);
    if (exactMatch) return exactMatch.value;

    // Check for case-insensitive match
    const caseInsensitiveMatch = validTypes.find(
      (type) => type.value.toLowerCase() === amenity.toLowerCase()
    );
    if (caseInsensitiveMatch) return caseInsensitiveMatch.value;

    // Return original value as fallback
    return amenity;
  });
};

export const parseBedroomsFromBE = (
  bedrooms: BedroomFromBE[],
  numberOfBedrooms: number = 0
) => {
  const retVal: Record<string, string | string[] | number | boolean> = {};
  for (let i = 1; i <= bedrooms.length; i++) {
    retVal[`bedroom${i}Info`] = bedrooms[i - 1].info ?? "";
    for (let j = 1; j <= bedrooms[i - 1].beds.length; j++) {
      retVal[`bedroom${i}Quantity${j === 1 ? "" : j}`] =
        bedrooms[i - 1].beds[j - 1].quantity;
      retVal[`bedroom${i}Type${j === 1 ? "" : j}`] = normalizeBedType(
        bedrooms[i - 1].beds[j - 1].type
      );
    }
  }
  // Scaffold empty fields for Streamline-synced properties that have a bedroom
  // count but no detail records, so the edit form can render editable inputs.
  for (let i = bedrooms.length + 1; i <= numberOfBedrooms; i++) {
    retVal[`bedroom${i}Type`] = "";
    retVal[`bedroom${i}Quantity`] = 1;
    retVal[`bedroom${i}Info`] = "";
  }
  return retVal;
};

// A bathroom is "half" when either its type or (when type is null in the synced
// payload) its name says so — e.g. name "Half Bathroom 6", type null. Anything
// else is treated as a full bathroom. Matching on `type` alone silently dropped
// every synced bathroom, since the sync leaves `type` null (MSMS bathroom bug).
export const isHalfBathroom = (bathroom: {
  type?: string | null;
  name?: string | null;
}): boolean =>
  (bathroom.type ?? bathroom.name ?? "").toLowerCase().includes("half");

export const parseBathroomsFromBE = (
  bathrooms: BathroomFromBE[],
  bedrooms: BedroomFromBE[],
  numberOfFullBathrooms: number = 0,
  numberOfHalfBathrooms: number = 0
) => {
  const retVal: Record<string, string | string[]> = {};
  let fullBathroomCount = 0;
  let halfBathroomCount = 0;
  for (const bathroom of bathrooms) {
    const linkedBedrooms = bedrooms.flatMap((bedroom) =>
      bedroom.bathrooms.filter((b) => b.bathroomId === bathroom.id).length > 0
        ? [bedroom.name]
        : []
    );
    if (isHalfBathroom(bathroom)) {
      retVal[`halfBathroom${halfBathroomCount + 1}Type`] =
        normalizeBathroomAmenities(bathroom.amenities, true);
      retVal[`halfBathroom${halfBathroomCount + 1}Info`] =
        bathroom.description ?? "";
      retVal[`halfBathroom${halfBathroomCount + 1}Bedrooms`] = linkedBedrooms;
      halfBathroomCount++;
    } else {
      retVal[`fullBathroom${fullBathroomCount + 1}Type`] =
        normalizeBathroomAmenities(bathroom.amenities, false);
      retVal[`fullBathroom${fullBathroomCount + 1}Info`] =
        bathroom.description ?? "";
      retVal[`fullBathroom${fullBathroomCount + 1}Bedrooms`] = linkedBedrooms;
      fullBathroomCount++;
    }
  }
  // Scaffold empty fields for Streamline-synced properties that have bathroom
  // counts but no detail records, so the edit form can render editable inputs.
  for (let i = fullBathroomCount + 1; i <= numberOfFullBathrooms; i++) {
    retVal[`fullBathroom${i}Type`] = [];
    retVal[`fullBathroom${i}Info`] = "";
    retVal[`fullBathroom${i}Bedrooms`] = [];
  }
  for (let i = halfBathroomCount + 1; i <= numberOfHalfBathrooms; i++) {
    retVal[`halfBathroom${i}Type`] = [];
    retVal[`halfBathroom${i}Info`] = "";
    retVal[`halfBathroom${i}Bedrooms`] = [];
  }
  return retVal;
};

export const convertPropertyFromBEToOnboard = (
  property: PropertyFromBE
): OnboardProperty => {
  // Render as many bathroom sections as there are synced rooms, even if the
  // scalar count fields (numberOfFullBathrooms/numberOfHalfBathrooms) lag behind
  // the actual bathroom records — otherwise persisted bathrooms stay hidden.
  const fullBathroomsFromArray = property.bathrooms.filter(
    (bathroom) => !isHalfBathroom(bathroom)
  ).length;
  const halfBathroomsFromArray = property.bathrooms.filter((bathroom) =>
    isHalfBathroom(bathroom)
  ).length;
  const fullBathroomsCount = Math.max(
    property.numberOfFullBathrooms ?? 0,
    fullBathroomsFromArray
  );
  const halfBathroomsCount = Math.max(
    property.numberOfHalfBathrooms ?? 0,
    halfBathroomsFromArray
  );

  return {
    id: property.id,
    // Sorted so the form's dirty check is order-independent (add-then-remove
    // of the same tag won't read as a change).
    tagIds: [...(property.tags?.map((tag) => tag.id) ?? [])].sort(),
    isListed: !!property.propertyLink1 && !!property.propertyLink2,
    propertyLink1: property.propertyLink1 ?? "",
    propertyLink2: property.propertyLink2 ?? "",
    propertyName: property.name ?? "",
    propertyType: property.type ?? "",
    bedrooms: property.numberOfBedrooms ?? 0,
    bathrooms: fullBathroomsCount + halfBathroomsCount,
    bedroomFields: parseBedroomsFromBE(property.bedrooms, property.numberOfBedrooms ?? 0),
    bathroomFields: parseBathroomsFromBE(
      property.bathrooms,
      property.bedrooms,
      fullBathroomsCount,
      halfBathroomsCount
    ),
    maxGuests: 1,
    parkingSpaces: property.numberOfParkingSpots ?? 2,
    // Keep the default of 1 full bath for a brand-new/empty property (render and
    // the count selector both treat 0 as 1), while honoring the real count.
    fullBathrooms: fullBathroomsCount || 1,
    halfBathrooms: halfBathroomsCount,
    amenities: {
      groups: [],
      amenities: property.streamlineAmenities ?? [],
    },
    wifiName: property.wifiName ?? "",
    wifiPassword: property.wifiPassword ?? "",
    isPetFriendly: property.petFriendly ?? false,
    maxPets: property.maxPets ?? null,
    description: property.description ?? "",
    pestControlCompany: property.pestControlCompanyName ?? "",
    pestControlPhone: property.pestControlCompanyPhoneNumber ?? "",
    poolCompany: property.poolCompanyName ?? "",
    poolPhone: property.poolCompanyPhoneNumber ?? "",
    poolService: property.poolServiceDay ?? [],
    hvacCompany: property.hvac ?? "",
    hvacPhone: property.hvacPhoneNumber ?? "",
    hvacService: property.hvacTrashPickupDay ?? [],
    houseManagerName: property.houseManagerName ?? "",
    houseManagerPhone: property.houseManagerPhoneNumber ?? "",
    landscapingCompany: property.landscapingCompanyName ?? "",
    landscapingPhone: property.landscapingCompanyPhoneNumber ?? "",
    landscapingService: property.landscapingServiceDay ?? [],
    additionalVendors: property.additionalVendors.map((vendor) => ({
      type: vendor.type,
      name: vendor.companyName,
      phone: vendor.companyPhoneNumber,
      info: vendor.otherInformation,
    })),
    streetAddress: property.streetAddress ?? "",
    apartmentUnit: property.apartment ?? "",
    state: (() => {
      if (!property.state) return "";

      // Check if backend returned a state code (e.g., "CA", "FL")
      if (USStates[property.state as keyof typeof USStates]) {
        return property.state;
      }

      // Check if backend returned a full state name (e.g., "California", "Florida")
      if (USStatesByName[property.state as keyof typeof USStatesByName]) {
        return USStatesByName[property.state as keyof typeof USStatesByName];
      }

      // Fallback: try case-insensitive match
      const stateCode = Object.keys(USStates).find(
        (key) => USStates[key as keyof typeof USStates] === property.state
      );

      return stateCode ?? "";
    })(),
    city: property.city ?? "",
    zipCode: property.zipCode ?? "",
    country: property.country ?? "",
    size: property.size ?? 0,
    totalBeds: property.sleepTotal ?? 0,
    guestCleaning: property.guestCleaningService ?? false,
    smartLock: (() => {
      if (property.smartLock && !property.lockPin) {
        return SmartLockType.INTEGRATION;
      }
      if (!property.smartLock && property.lockPin) {
        // Streamline's lock_box_code can be freeform text (e.g. "Gate Code: 5577, ...").
        // Only treat it as STATIC_PIN when the value is actually a short numeric PIN,
        // otherwise the strict lockPin validation would block the edit form.
        const isNumericPin = /^[0-9]+$/.test(property.lockPin) && property.lockPin.length <= 12;
        return isNumericPin ? SmartLockType.STATIC_PIN : SmartLockType.INTEGRATION;
      }
      if (!property.smartLock && !property.lockPin) {
        return SmartLockType.LAST_FOUR_DIGITS;
      }
      return SmartLockType.INTEGRATION;
    })(),
    lockPin: property.lockPin ?? "",
    damageWaiver: property.damageWaiverService ?? false,
    propertyUnits: property.units.map((unit, index) => ({
      id: unit.id,
      index: index,
      title: unit.name,
      propertyType: "Apartment",
      unitDescription: unit.description,
      size: unit.size,
      totalBeds: unit.sleepTotal,
      bedrooms: unit.numberOfBedrooms,
      fullBathrooms: unit.numberOfFullBathrooms,
      halfBathrooms: unit.numberOfHalfBathrooms ?? 0,
      parkingSpaces: unit.numberOfParkingSpots ?? 0,
      amenities: {
        groups: [],
        amenities: unit?.streamlineAmenities ?? [],
      },
      isPetFriendly: unit.petFriendly,
      maxPets: property.maxPets ?? null,
      wifiName: unit.wifiName ?? "",
      wifiPassword: unit.wifiPassword ?? "",
      ...parseBedroomsFromBE(unit.bedrooms, unit.numberOfBedrooms ?? 0),
      ...parseBathroomsFromBE(unit.bathrooms, unit.bedrooms, unit.numberOfFullBathrooms ?? 0, unit.numberOfHalfBathrooms ?? 0),
    })),
    status: property.status,
    monthBlockNotices: [],
    yearBlockNotices: {},
    updatedAt: property.updatedAt,
  };
};

export const processBathrooms = (values: PropertyFormValues | PropertyUnit) => {
  const bathrooms = [];
  for (let i = 1; i <= values.fullBathrooms; i++) {
    const bathroom = {
      type: "Full Bathroom",
      name: `Full Bathroom ${i}`,
      amenities: [values[`fullBathroom${i}Type`]].flat(),
      description: values[`fullBathroom${i}Info`],
      bedrooms: (values[`fullBathroom${i}Bedrooms`] || []).filter(
        (bedroom: string) => bedroom.trim().toLowerCase() !== "no"
      ),
    };
    bathrooms.push(bathroom);
  }
  for (let i = 1; i <= values.halfBathrooms; i++) {
    const bathroom = {
      type: "Half Bathroom",
      name: `Half Bathroom ${i}`,
      amenities: [values[`halfBathroom${i}Type`]].flat(),
      description: values[`halfBathroom${i}Info`],
      bedrooms: (values[`halfBathroom${i}Bedrooms`] || []).filter(
        (bedroom: string) => bedroom.trim().toLowerCase() !== "no"
      ),
    };
    bathrooms.push(bathroom);
  }
  return bathrooms;
};

export const processBedrooms = (values: PropertyFormValues | PropertyUnit) => {
  const bedrooms = [];
  for (let i = 1; i <= values.bedrooms; i++) {
    const beds: Array<{ type: string; quantity: number }> = [];
    beds.push({
      type: values[`bedroom${i}Type`],
      quantity: values[`bedroom${i}Quantity`] || 1,
    });
    let j = 2;
    while (values[`bedroom${i}Type${j}`]) {
      beds.push({
        type: values[`bedroom${i}Type${j}`] as string,
        quantity: (values[`bedroom${i}Quantity${j}`] as number) || 1,
      });
      j++;
    }

    const bedroomData: {
      name: string;
      beds: Array<{ type: string; quantity: number }>;
      info: string | null;
      bathrooms?: Array<{
        type: string;
        amenities: string[];
      }>;
    } = {
      name: `Bedroom ${i}`,
      info: (values[`bedroom${i}Info`] as string) || null,
      beds,
    };
    bedrooms.push(bedroomData);
  }
  return bedrooms;
};

export const processSmartLock = (values: PropertyFormValues) => {
  switch (values.smartLock) {
    case SmartLockType.INTEGRATION:
      return {
        smartLock: true,
        // Preserve an existing lockPin (e.g. Streamline lock_box_code) even
        // when the UI shows "Integration" — avoids wiping freeform lock info.
        lockPin: values.lockPin || null,
      };
    case SmartLockType.STATIC_PIN:
      return {
        smartLock: false,
        lockPin: values.lockPin,
      };
    case SmartLockType.LAST_FOUR_DIGITS:
      return {
        smartLock: false,
        lockPin: null,
      };
    default:
      return {
        smartLock: false,
        lockPin: null,
      };
  }
};

export const processAdditionalVendors = (values: PropertyFormValues) => {
  return values.additionalVendors.map((vendor) => ({
    type: vendor.type,
    companyName: vendor.name || null,
    companyPhoneNumber: vendor.phone || null,
    otherInformation: vendor.info || null,
  }));
};

// Links are stored absolute, so one typed without a scheme is assumed https.
export const toAbsoluteUrl = (
  link: string | null | undefined
): string | null => {
  if (!link) return null;
  if (link.startsWith("http")) return link;

  return `https://${link}`;
};

// Nothing is stored for a property that isn't listed.
export const normalizePropertyLink = (
  link: string | null | undefined,
  isListed: boolean | undefined
): string | null => (isListed ? toAbsoluteUrl(link) : null);

export const processUnits = (propertyUnits: PropertyUnit[]) => {
  if (!propertyUnits?.length) return [];

  return propertyUnits.map((unit) => {
    const bathrooms = processBathrooms(unit);
    const bedrooms = processBedrooms(unit);

    return {
      id: unit.id,
      name: unit.title,
      size: unit.size,
      numberOfBedrooms: unit.bedrooms,
      numberOfHalfBathrooms: unit.halfBathrooms,
      numberOfFullBathrooms: unit.fullBathrooms,
      numberOfParkingSpots: unit.parkingSpaces ?? 0,
      sleepTotal: unit.totalBeds,

      wifiName: unit.wifiName || null,
      wifiPassword: unit.wifiPassword || null,
      streamlineAmenities: unit.amenities.amenities.flatMap((a: Amenity) =>
        a.selected ? [a.id] : []
      ),

      petFriendly: unit.isPetFriendly,
      description: unit.unitDescription,
      bedrooms,
      bathrooms,
    };
  });
};

export const convertPropertyFormValuesToUpdateBody = (
  id: string,
  values: PropertyFormValues
) => {
  const bathrooms = processBathrooms(values);
  const bedrooms = processBedrooms(values);
  const additionalVendors = processAdditionalVendors(values);
  const units = processUnits(values.propertyUnits || []);
  const { smartLock, lockPin } = processSmartLock(values);

  return {
    id,
    // Always sent so an empty array clears all tags on the property.
    tagIds: values.tagIds,
    name: values.propertyName,
    propertyLink1: normalizePropertyLink(values.propertyLink1, values.isListed),
    propertyLink2: normalizePropertyLink(values.propertyLink2, values.isListed),
    streetAddress: values.streetAddress,
    apartment: values.apartmentUnit || null,
    state: USStates[values.state as keyof typeof USStates],
    city: values.city,
    zipCode: values.zipCode,
    country: values.country,
    size: values.size,
    numberOfBedrooms: values.bedrooms,
    numberOfHalfBathrooms: values.halfBathrooms,
    numberOfFullBathrooms: values.fullBathrooms,
    numberOfParkingSpots: values.parkingSpaces,
    sleepTotal: values.totalBeds,
    wifiName: values.wifiName || null,
    wifiPassword: values.wifiPassword || null,
    petFriendly: values.isPetFriendly,
    maxPets: values.maxPets,
    description: values.description || null,
    pestControlCompanyName: values.pestControlCompany || null,
    pestControlCompanyPhoneNumber: values.pestControlPhone || "",
    poolCompanyName: values.poolCompany || null,
    poolCompanyPhoneNumber: values.poolPhone || "",
    poolServiceDay: [values.poolService].flat() || null,
    hvac: values.hvacCompany || null,
    hvacPhoneNumber: values.hvacPhone || "",
    hvacTrashPickupDay: [values.hvacService].flat() || null,
    houseManagerName: values.houseManagerName || null,
    houseManagerPhoneNumber: values.houseManagerPhone || "",
    landscapingCompanyName: values.landscapingCompany || null,
    landscapingCompanyPhoneNumber: values.landscapingPhone || "",
    landscapingServiceDay: [values.landscapingService].flat() || null,
    guestCleaningService: values.guestCleaning,
    smartLock,
    lockPin,
    damageWaiverService: values.damageWaiver,
    units,
    bedrooms: values.propertyType === PropertyType.DUPLEX ? [] : bedrooms,
    bathrooms: values.propertyType === PropertyType.DUPLEX ? [] : bathrooms,
    additionalVendors,
    streamlineAmenities: values.amenities.amenities.flatMap((a: Amenity) =>
      a.selected ? [a.id] : []
    ),
  };
};

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

// Iterates through an object and updates the streamlineAmenities arrays using the sent mapFunction
export const updateStreamlineAmenities = (
  obj: JsonValue,
  mapFunction: (amenity: string) => string
): JsonValue => {
  if (Array.isArray(obj)) {
    return obj.map((item) => updateStreamlineAmenities(item, mapFunction));
  }

  if (obj !== null && typeof obj === "object") {
    const newObj: { [key: string]: JsonValue } = {};

    for (const key in obj) {
      const value = obj[key];

      if (key === "streamlineAmenities" && Array.isArray(value)) {
        newObj[key] = value.map((amenity) => mapFunction(amenity as string));
      } else {
        newObj[key] = updateStreamlineAmenities(value, mapFunction);
      }
    }

    return newObj;
  }

  return obj;
};
